// Copyright (c) 2021 WiseTime. All rights reserved.

import { concat, EMPTY, Observable, of } from "rxjs"
import { catchError, filter, map, mergeMap } from "rxjs/operators"
import { Action } from "redux"
import * as Actions from "../actions"
import * as Slice from "../slice"
import * as UserContextSlice from "../../user-context/slice"
import * as UserContextActions from "../../user-context/actions"
import { ListFavouriteTagsRequest, ListFavouriteTagsResponse, } from "generated/legebill/site/tag/v4/tag_pb"
import { grpc } from "app/rpc/executor"
import { tagServiceClient } from "app/rpc/client"
import { throttleSame } from "../../../lib/rxutils"
import { isGrpcError } from "../../../lib/rpcutils"
import { StatusCode } from "grpc-web"
import { t } from "@lingui/macro"
import { showSnackBar } from "../../snackbar/actions"

type FetchFavouriteTagsRpc = ({ siteId }: Actions.FetchFavouriteTagsPayload) => Observable<ListFavouriteTagsResponse>

export const fetchFavouriteTags = (fetchFavouriteTagsRpc: FetchFavouriteTagsRpc) =>
  (action$: Observable<Action>): Observable<Action> =>
    action$.pipe(
      filter(Actions.fetchFavouriteTags.match),
      throttleSame(2000, ({ payload: a }, { payload: b }) => a.siteId === b.siteId),
      mergeMap(({ payload }) =>
        of(payload)
          .pipe(
            mergeMap(fetchFavouriteTagsRpc),
            map(t => t.toObject()),
            map(Slice.setFavouriteTags),
            catchError(e => handleError(e, payload.swallowFailure)),
          )
      ),
    )

const fetchFavouriteTagsRpc = ({ siteId }: Actions.FetchFavouriteTagsPayload): Observable<ListFavouriteTagsResponse> =>
  grpc(() => {
    const request = new ListFavouriteTagsRequest().setSiteId(siteId)
    return tagServiceClient.listFavouriteTags(request, null)
  })

const handleError = (error: unknown, swallowFailure: boolean | undefined): Observable<Action> => {
  if (isGrpcError(error)) {
    const { code } = error
    if (code === StatusCode.PERMISSION_DENIED) {
      // clear current site id and re-fetch user (includes sites list) and membership details
      return concat(
        of(UserContextSlice.clearCurrentSiteId()),
        of(UserContextActions.fetchUserDetails()),
      )
    }
  }

  return swallowFailure ?
    EMPTY :
    of(showSnackBar({
      message: t({ id: "msg_fetch_favourite_tags_failed", message: "Failed to load favourite tags. Please try again in a couple of minutes. If the error persists, please contact support@wisetime.com." }),
      messageType: "ERROR",
    }))
}

export default fetchFavouriteTags(fetchFavouriteTagsRpc)
